import { Controller } from "@hotwired/stimulus"

declare const google: {
  accounts: {
    oauth2: {
      initTokenClient(config: {
        client_id: string
        scope: string
        callback: (response: TokenResponse) => void
      }): TokenClient
    }
  }
  picker: {
    PickerBuilder: new () => PickerBuilder
    ViewId: { SPREADSHEETS: string }
    View: new (viewId: string) => PickerView
    Action: { PICKED: string; CANCEL: string }
  }
}

declare namespace gapi {
  function load(apiName: string, callback: () => void): void;
}

interface TokenResponse {
  access_token: string
  expires_in: number
  error?: string
}

interface TokenClient {
  requestAccessToken(): void
}

interface PickerBuilder {
  addView(view: PickerView): PickerBuilder
  setOAuthToken(token: string): PickerBuilder
  setDeveloperKey(key: string): PickerBuilder
  setAppId(id: string): PickerBuilder
  setOrigin(origin: string): PickerBuilder
  setCallback(fn: (data: PickerResponse) => void): PickerBuilder
  setSize(width: number, height: number): PickerBuilder
  build(): Picker
}

interface PickerView {
  // opaque
}

interface Picker {
  setVisible(visible: boolean): void
}

interface PickerResponse {
  action: string
  docs?: Array<{ id: string; name: string }>
}

/**
 * google-picker
 *
 * Loads GIS and the Picker API on demand, requests a scoped Drive access
 * token (cached in memory until it expires), opens a Picker restricted to
 * spreadsheets, and POSTs the chosen file_id + access_token to the backend.
 *
 * Values [data-google-picker-*]:
 *   client-id-value     – OAuth2 client ID
 *   developer-key-value – Google API key (for the Picker widget)
 *   app-id-value        – Google Cloud project number (for Drive scope)
 *   upload-url-value    – Backend endpoint (POST)
 *   csrf-token-value    – Rails CSRF token for the POST request
 */
export default class extends Controller {
  static targets = ['publicUrlPanel', 'pickerPanel']
  static values = {
    clientId:     String,
    developerKey: String,
    appId:        String,
    uploadUrl:    String,
    csrfToken:    String,
  }

  declare publicUrlPanelTarget: HTMLElement
  declare pickerPanelTarget:    HTMLElement
  declare clientIdValue:     string
  declare developerKeyValue: string
  declare appIdValue:        string
  declare uploadUrlValue:    string
  declare csrfTokenValue:    string

  private accessToken: string | null = null
  private tokenExpiresAt: number = 0
  private tokenClient: TokenClient | null = null
  private gisLoaded = false
  private pickerLoaded = false

  connect(): void {
    this.loadScript("https://accounts.google.com/gsi/client",   () => { this.gisLoaded    = true })
    this.loadScript("https://apis.google.com/js/api.js", () => {
      gapi.load("picker", () => { this.pickerLoaded = true })
    })
  }

  disconnect(): void {
    this.accessToken  = null
    this.tokenExpiresAt = 0
    this.tokenClient  = null
  }

  /** Called when either import-method radio changes. */
  selectMethod(event: Event): void {
    const radio = event.target as HTMLInputElement
    const isPublicUrl = radio.value === 'public_url'
    this.publicUrlPanelTarget.hidden = !isPublicUrl
    this.pickerPanelTarget.hidden    = isPublicUrl

    // Clear any lingering error message from a previous attempt.
    document.getElementById('voter_list_notices')?.remove()
  }

  /** Called by the "Choose from Google Drive" button (data-action="click->google-picker#open"). */
  open(): void {
    if (this.hasValidToken()) {
      this.openPicker()
    } else {
      this.requestToken()
    }
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private hasValidToken(): boolean {
    return this.accessToken !== null && Date.now() < this.tokenExpiresAt
  }

  private requestToken(retryCount = 0): void {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 500;

    if (typeof google === "undefined" || !google?.accounts?.oauth2) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => {
          this.requestToken(retryCount + 1);
        }, RETRY_DELAY_MS);

        return;
      } else {
        console.error("[google-picker] Failed to load Google Identity Services after multiple attempts.");
        return;
      }
    }

    if (!this.tokenClient) {
      this.tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: this.clientIdValue,
        scope:     "https://www.googleapis.com/auth/drive.file",
        callback:  (response: TokenResponse) => {
          if (response.error) {
            console.error("[google-picker] token error:", response.error);
            return;
          }
          // Cache with a 30-second safety margin.
          this.accessToken   = response.access_token;
          this.tokenExpiresAt = Date.now() + (response.expires_in - 30) * 1000;
          this.openPicker();
        },
      });
    }

    this.tokenClient.requestAccessToken();
  }

  private openPicker(): void {
    if (!this.pickerLoaded) {
      setTimeout(() => this.openPicker(), 500)
      return
    }

    const view = new google.picker.View(google.picker.ViewId.SPREADSHEETS)

    const picker = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken(this.accessToken!)
      .setDeveloperKey(this.developerKeyValue)
      .setAppId(this.appIdValue)
      .setOrigin(window.location.origin)
      .setCallback((data: PickerResponse) => this.onPicked(data))
      .setSize(800, 600)
      .build()

    picker.setVisible(true)
  }

  private onPicked(data: PickerResponse): void {
    if (data.action !== google.picker.Action.PICKED) return
    if (!data.docs?.length) return

    const fileId = data.docs[0].id
    this.submitToBackend(fileId)
  }

  private submitToBackend(fileId: string): void {
    // Build a hidden remote form so Rails UJS evaluates the .js.erb response.
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.uploadUrlValue
    form.dataset["remote"] = "true"

    const fields: Record<string, string> = {
      authenticity_token: this.csrfTokenValue,
      source:             "picker",
      file_id:            fileId,
      access_token:       this.accessToken!,
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement("input")
      input.type  = "hidden"
      input.name  = name
      input.value = value
      form.appendChild(input)
    })

    document.body.appendChild(form)
    // Dispatch the event so Rails UJS intercepts it (form.submit() bypasses UJS listeners).
    form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }))
    document.body.removeChild(form)
  }

  /** Injects an external script tag once per URL. */
  private loadScript(src: string, onLoad: () => void): void {
    const existingScript = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null

    if (existingScript) {
      if (existingScript.dataset["loaded"] === "true") {
        onLoad()
      } else {
        existingScript.addEventListener("load", onLoad, { once: true })
      }
      return
    }
    const script = document.createElement("script")
    const handleLoad = (): void => {
      script.dataset["loaded"] = "true"
      onLoad()
    }
    script.src = src
    script.async = true
    script.addEventListener("load", handleLoad, { once: true })
    document.head.appendChild(script)
  }
}
